import React from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import Header from '../components/Header/Header';
import './AboutPage.css';

const AboutPage = () => {
  const { currentLanguage } = useSelector((state) => state.language);
  
  const companyInfo = {
    mission: {
      en: 'To provide reliable, transparent, and customer-focused insurance solutions that build trust and ensure peace of mind.',
      ar: 'تقديم حلول تأمينية موثوقة وشفافية ومركزة على العميل تبني الثقة وتضمن راحة البال.'
    },
    vision: {
      en: 'To become the most trusted insurance partner in Libya, known for innovation, integrity, and exceptional service.',
      ar: 'أن نصبح الشريك التأميني الأكثر ثقة في ليبيا، معروفين بالابتكار والنزاهة والخدمة الاستثنائية.'
    },
    values: [
      {
        title: { en: 'Integrity', ar: 'النزاهة' },
        description: { 
          en: 'We operate with honesty and transparency in all our dealings.', 
          ar: 'نعمل بصدق وشفافية في جميع تعاملاتنا.' 
        },
        icon: '🤝'
      },
      {
        title: { en: 'Trust', ar: 'الثقة' },
        description: { 
          en: 'We build lasting relationships based on reliability and commitment.', 
          ar: 'نبني علاقات دائمة قائمة على الموثوقية والالتزام.' 
        },
        icon: '🛡️'
      },
      {
        title: { en: 'Innovation', ar: 'الابتكار' },
        description: { 
          en: 'We continuously improve our services through technology and creativity.', 
          ar: 'نحسن خدماتنا باستمرار من خلال التكنولوجيا والإبداع.' 
        },
        icon: '💡'
      },
      {
        title: { en: 'Customer Focus', ar: 'التركيز على العميل' },
        description: { 
          en: 'We prioritize our customers\' needs and exceed their expectations.', 
          ar: 'نركز على احتياجات عملائنا ونفوق توقعاتهم.' 
        },
        icon: '👥'
      }
    ],
    milestones: [
      {
        year: '2022',
        title: { en: 'Company Founded', ar: 'تأسيس الشركة' },
        description: { 
          en: 'Sanad Insurance was established with a vision to modernize the insurance sector in Libya.', 
          ar: 'تأسست شركة سند للتأمين برؤية لتحديث قطاع التأمين في ليبيا.' 
        }
      },
      {
        year: '2023',
        title: { en: 'License Obtained', ar: 'الحصول على الترخيص' },
        description: { 
          en: 'Received official license from the Libyan Insurance Authority.', 
          ar: 'الحصول على الترخيص الرسمي من هيئة التأمين الليبية.' 
        }
      },
      {
        year: '2024',
        title: { en: 'Digital Platform Launch', ar: 'إطلاق المنصة الرقمية' },
        description: { 
          en: 'Launched our digital platform and expanded services to 13 insurance types.', 
          ar: 'إطلاق منصتنا الرقمية وتوسيع الخدمات إلى 13 نوع تأمين.' 
        }
      }
    ],
    team: [
      {
        name: 'عمر الفيتوري',
        position: { en: 'CEO & Founder', ar: 'المدير التنفيذي والمؤسس' },
        bio: { 
          en: 'Over 15 years of experience in insurance and risk management.', 
          ar: 'أكثر من 15 سنة خبرة في مجال التأمين وإدارة المخاطر.' 
        },
        image: '👨‍💼'
      },
      {
        name: 'فاطمة القذافي',
        position: { en: 'Operations Director', ar: 'مديرة العمليات' },
        bio: { 
          en: 'Specialized in insurance operations and customer service excellence.', 
          ar: 'متخصصة في عمليات التأمين وتميز خدمة العملاء.' 
        },
        image: '👩‍💼'
      },
      {
        name: 'خالد السعيدي',
        position: { en: 'Technical Director', ar: 'المدير الفني' },
        bio: { 
          en: 'Expert in insurance underwriting and risk assessment.', 
          ar: 'خبير في الاكتتاب التأميني وتقييم المخاطر.' 
        },
        image: '👨‍🔬'
      }
    ]
  };

  return (
    <div className="about-page">
      <Header />
      
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-content"
          >
            <h1>
              {currentLanguage === 'en' 
                ? 'About Sanad Insurance' 
                : 'عن شركة سند للتأمين'}
            </h1>
            <p>
              {currentLanguage === 'en' 
                ? 'Your trusted partner in protection and peace of mind' 
                : 'شريكك الموثوق في الحماية وراحة البال'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="company-story">
        <div className="container">
          <div className="story-content">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="story-text"
            >
              <h2>{currentLanguage === 'en' ? 'Our Story' : 'قصتنا'}</h2>
              <p>
                {currentLanguage === 'en' 
                  ? `Sanad Insurance was founded in 2022 by experienced professionals in the insurance industry who recognized the need for modern, transparent, and customer-focused insurance solutions in Libya. With decades of combined experience in risk management, underwriting, and customer service, our founders set out to create a company that would redefine insurance standards in the region.

                  As a newly established company, we bring fresh perspectives while maintaining the wisdom of traditional insurance practices. We are officially registered with the Libyan Insurance Authority and operate with full compliance to all regulatory requirements.

                  Our name "Sanad" (meaning "support" or "backing") reflects our commitment to being a reliable support system for our clients during uncertain times. We believe that insurance should not be complicated or intimidating – it should be a straightforward partnership based on trust and mutual understanding.` 
                  : `تأسست شركة سند للتأمين عام 2022 من قبل محترفين ذوي خبرة في صناعة التأمين الذين أدركوا الحاجة إلى حلول تأمينية حديثة وشفافة ومركزة على العميل في ليبيا. بعقود من الخبرة المشتركة في إدارة المخاطر والاكتتاب وخدمة العملاء، شرع مؤسسونا في إنشاء شركة تعيد تعريف معايير التأمين في المنطقة.

                  كشركة ناشئة، نقدم وجهات نظر جديدة مع الحفاظ على حكمة الممارسات التأمينية التقليدية. نحن مسجلون رسميًا لدى هيئة التأمين الليبية ونعمل وفقًا لمتطلبات جميع اللوائح التنظيمية.

                  يعكس اسمنا "سند" (بمعنى "دعم" أو "مساندة") التزامنا بأن نكون نظام دعم موثوقًا لعملائنا خلال الأوقات غير المؤكدة. نحن نؤمن بأن التأمين لا ينبغي أن يكون معقدًا أو مخيفًا – يجب أن يكون شراكة مباشرة تقوم على الثقة والتفاهم المتبادل.`}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="story-stats"
            >
              <div className="stat-item">
                <h3>13+</h3>
                <p>{currentLanguage === 'en' ? 'Insurance Types' : 'نوع تأمين'}</p>
              </div>
              <div className="stat-item">
                <h3>15,000+</h3>
                <p>{currentLanguage === 'en' ? 'Protected Clients' : 'عميل محمي'}</p>
              </div>
              <div className="stat-item">
                <h3>24/7</h3>
                <p>{currentLanguage === 'en' ? 'Support' : 'دعم'}</p>
              </div>
              <div className="stat-item">
                <h3>100%</h3>
                <p>{currentLanguage === 'en' ? 'Compliance' : 'التزام'}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-vision">
        <div className="container">
          <div className="mv-grid">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mv-card mission"
            >
              <div className="mv-icon">🎯</div>
              <h3>{currentLanguage === 'en' ? 'Our Mission' : 'مهمتنا'}</h3>
              <p>{companyInfo.mission[currentLanguage]}</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="mv-card vision"
            >
              <div className="mv-icon">👁️</div>
              <h3>{currentLanguage === 'en' ? 'Our Vision' : 'رؤيتنا'}</h3>
              <p>{companyInfo.vision[currentLanguage]}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="core-values">
        <div className="container">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">
              {currentLanguage === 'en' ? 'Our Core Values' : 'قيمنا الأساسية'}
            </h2>
            
            <div className="values-grid">
              {companyInfo.values.map((value, index) => (
                <motion.div
                  key={index}
                  className="value-card"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10, boxShadow: '0 15px 30px rgba(0, 0, 0, 0.1)' }}
                >
                  <div className="value-icon">{value.icon}</div>
                  <h3>{value.title[currentLanguage]}</h3>
                  <p>{value.description[currentLanguage]}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Company Milestones */}
      <section className="milestones">
        <div className="container">
          <h2 className="section-title">
            {currentLanguage === 'en' ? 'Our Journey' : 'رحلتنا'}
          </h2>
          
          <div className="timeline">
            {companyInfo.milestones.map((milestone, index) => (
              <motion.div
                key={index}
                className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="timeline-year">{milestone.year}</div>
                <div className="timeline-content">
                  <h3>{milestone.title[currentLanguage]}</h3>
                  <p>{milestone.description[currentLanguage]}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="leadership-team">
        <div className="container">
          <h2 className="section-title">
            {currentLanguage === 'en' ? 'Leadership Team' : 'فريق القيادة'}
          </h2>
          
          <div className="team-grid">
            {companyInfo.team.map((member, index) => (
              <motion.div
                key={index}
                className="team-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className="team-image">{member.image}</div>
                <h3>{member.name}</h3>
                <p className="position">{member.position[currentLanguage]}</p>
                <p className="bio">{member.bio[currentLanguage]}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2>
              {currentLanguage === 'en' 
                ? 'Join Us on Our Journey' 
                : 'انضم إلينا في رحلتنا'}
            </h2>
            <p>
              {currentLanguage === 'en' 
                ? 'Be part of a new era in Libyan insurance. Experience protection you can trust.' 
                : 'كن جزءًا من عصر جديد في التأمين الليبي. جرب حماية يمكنك الوثوق بها.'}
            </p>
            <motion.button
              className="cta-button"
              whileHover={{ scale: 1.05, boxShadow: '0 10px 25px rgba(109, 40, 217, 0.3)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = '/contact'}
            >
              {currentLanguage === 'en' ? 'Get in Touch' : 'تواصل معنا'}
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;